import { PDFDocument } from 'pdf-lib'
import { MAX_SEGMENT_BYTES } from './segments'
import { cropBox, toBlob } from './rasterize'
import { isPdf } from './image-source'

// Scanned pages are drawn into a PDF at 200 DPI.
const POINTS_PER_PIXEL = 72 / 200

const baseName = (fileName) => fileName.replace(/\.[^.]+$/, '')
const extensionOf = (fileName) => fileName.match(/\.([^.]+)$/)?.[1]?.toLowerCase() || 'png'

const tooLarge = (name) => new Error(`${name} would be 10MB or more. Split it into smaller parts.`)

const checkedFile = (parts, name, type) => {
  const file = new File(parts, name, { type })
  if (file.size >= MAX_SEGMENT_BYTES) throw tooLarge(name)
  return file
}

const copiedPagesFile = async (source, pageNumbers, name) => {
  const doc = await PDFDocument.create({ updateMetadata: false })
  const pages = await doc.copyPages(source, pageNumbers.map((page) => page - 1))
  pages.forEach((page) => doc.addPage(page))
  return checkedFile([await doc.save()], `${name}.pdf`, 'application/pdf')
}

const pageImagesFile = async (images, name, pointsPerPixel = POINTS_PER_PIXEL) => {
  const doc = await PDFDocument.create({ updateMetadata: false })
  for (const { blob, width, height } of images) {
    const png = await doc.embedPng(await blob.arrayBuffer())
    const page = doc.addPage([width * pointsPerPixel, height * pointsPerPixel])
    page.drawImage(png, { x: 0, y: 0, width: page.getWidth(), height: page.getHeight() })
  }
  return checkedFile([await doc.save()], `${name}.pdf`, 'application/pdf')
}

// pdf-lib cannot copy pages out of an encrypted PDF, even one that opens without a password (common for
// system-generated invoices), so those pages are drawn from pdf.js renderings instead. Null means encrypted.
const loadPdf = async (file) => {
  const doc = await PDFDocument.load(await file.arrayBuffer(), { updateMetadata: false, ignoreEncryption: true })
  return doc.isEncrypted ? null : doc
}

const renderedPagesFile = async (renderPage, pageNumbers, name) => {
  const images = []
  let scale
  for (const pageNumber of pageNumbers) {
    const rendered = await renderPage(pageNumber)
    scale = rendered.scale
    const { canvas } = rendered
    images.push({ blob: await toBlob(canvas, 'image/png'), width: canvas.width, height: canvas.height })
  }
  return pageImagesFile(images, name, 1 / scale)
}

const isWholeSource = (pageNumbers, pageCount) =>
  pageNumbers.length === pageCount && pageNumbers.every((page, i) => page === i + 1)

// Builds one file per segment. From a PDF, whole pages are copied into a PDF as they are. From an image, a
// segment that is the whole file uploads it unchanged; one decoded page uploads as a PNG and several as a PDF of
// the page images. A boxed receipt becomes an upright image of just that box. PDF metadata is left unset so the
// same split always yields the same bytes, which keeps the server's duplicate-upload checksum working.
// renderPage(pageNumber) resolves to { canvas, scale } and is needed for boxed receipts and for an encrypted PDF;
// imageSource holds the decoded pages of an image file.
export const buildSegmentFiles = async (file, segments, { renderPage, imageSource } = {}) => {
  const fromPdf = isPdf(file)
  const needsPdf = fromPdf && segments.some((segment) => segment.pieces.some((piece) => !piece.box))
  const source = needsPdf ? await loadPdf(file) : null
  const pageCount = imageSource?.pageCount ?? 1
  let rendered = { page: null }
  const files = []

  for (const [i, segment] of segments.entries()) {
    const name = `${baseName(file.name)}_${i + 1}-of-${segments.length}`
    const [{ page, box }] = segment.pieces
    const pageNumbers = segment.pieces.map((piece) => piece.page)

    if (box) {
      if (rendered.page !== page) rendered = { page, ...(await renderPage(page)) }
      const crop = await cropBox(rendered, box, { maxBytes: MAX_SEGMENT_BYTES, preferJpeg: file.type === 'image/jpeg' })
      files.push(checkedFile([crop.blob], `${name}.${crop.extension}`, crop.type))
    } else if (fromPdf) {
      const pdf = source
        ? await copiedPagesFile(source, pageNumbers, name)
        : await renderedPagesFile(renderPage, pageNumbers, name)
      files.push(pdf)
    } else if (isWholeSource(pageNumbers, pageCount)) {
      files.push(checkedFile([file], `${name}.${extensionOf(file.name)}`, file.type))
    } else if (pageNumbers.length === 1) {
      files.push(checkedFile([imageSource.pages[page - 1].blob], `${name}.png`, 'image/png'))
    } else {
      files.push(await pageImagesFile(pageNumbers.map((n) => imageSource.pages[n - 1]), name))
    }
  }

  return files
}
